/*
 * TunePad
 *
 * Michael S. Horn
 * Northwestern University
 * michael-horn@northwestern.edu
 *
 * This project was funded by the National Science Foundation (grant DRL-1612619).
 * Any opinions, findings and conclusions or recommendations expressed in this
 * material are those of the author(s) and do not necessarily reflect the views
 * of the National Science Foundation (NSF).
 */
import { TunePadAudio, GrowableAudioBuffer, WaveformData, SoundLoader } from '../audio';

import styles from './sample.module.css' with { type: 'css' };
import html from './sample.module.html';


/**
 * ## TunePadSample cell lets users upload / record audio samples for playback in other cells
 * ```html
 * <tunepad-sample uuid = "{{ database UUID }}">
 * </tunepad-sample>
 * ```
 */
export class TunePadSample extends HTMLElement {

    static readonly ELEMENT = "tunepad-sample";

    static observedAttributes = [ "readonly" ];

    /** holds audio data */
    private buffer? : AudioBuffer;

    private absn? : AudioBufferSourceNode;

    private audio : TunePadAudio;

    private group : SVGGElement;

    /** all of the HTML elements are contained within a shadow DOM */
    private root : ShadowRoot;

    public get uuid() { return this.getAttribute('uuid') ?? 'sample' }

    /** full file path name */
    public get name() { return this.getAttribute('name') ?? '' }

    /** size of audio payload in base64 encoding */
    private sizeInBytes : number = 0;



    constructor() {
        super();
        this.root = this.attachShadow({ mode: 'open' });
        this.root.adoptedStyleSheets.push(styles);
        this.root.innerHTML = html;
        this.audio = TunePadAudio.init();
        this.group = this.root.querySelector('#wgroup') as SVGGElement;
    }


    async connectedCallback() {

        // event listeners
        const input = this.root.querySelector('#file-input') as HTMLInputElement;
        input.addEventListener('change', e => {
            if (input.files && input.files.length > 0) this.replaceFile(input.files[0]);
        });

        this.root.querySelector('#play-button')?.addEventListener('click', e => this.playPauseAudio());
        this.root.querySelector('#pause-button')?.addEventListener('click', e => this.playPauseAudio());

        this.root.querySelector('#upload-button')?.addEventListener('click', (e) => {
            e.stopPropagation();
            e.stopImmediatePropagation();
            input.click();
        });

        this.root.querySelector('#copy-button')?.addEventListener('click', e => {
            const code = this.root.querySelector('#code-hint')!.innerHTML;
            navigator.clipboard.writeText(code);
        });

        this.root.querySelector('#close-button')?.addEventListener('click', e => {
            if (window.confirm('Are you sure you want to delete this sound?')) {
                this.remove();
                this.deleteFileData();
                this.dispatchEvent(new CustomEvent("delete-sample", {
                    bubbles: false,
                    composed: true,
                    detail: { uuid : this.getAttribute('uuid'), name : this.name }
                }));
            }
        })
    }


    disconnectedCallback() { }

    attributeChangedCallback(name : string, oldValue : string, newValue : string) {
        if (newValue === oldValue) {
            return;
        }
        else if (name === 'readonly') {
            this.setReadOnly(newValue === 'true');
        }
    }

    private setReadOnly(ro : boolean) {
        this.setAttribute('readonly', `${ro}`);
        this.root.querySelector('#container')?.classList.toggle('readonly', ro);
    }


    private async loadArrayBuffer(abuf : ArrayBuffer) {
        this.addClass('#error', 'hidden');
        this.absn?.stop();
        this._elapsed = 0;
        this.setProgressBar(0);
        this.group.innerHTML = '';
        this.sizeInBytes = abuf.byteLength;

        try {
            this.buffer = await this.audio.context.decodeAudioData(abuf);

            // load into audio engine
            SoundLoader.sounds.set(`sample:${this.name}`, this.buffer);

            // generate waveform data
            const wdata = WaveformData.fromBuffer(this.buffer);
            const scale = 1000 / wdata.duration;
            this.group.setAttribute("transform", `translate(0, 25), scale(${scale}, 25)`);
            const path = wdata.wavepath;
            if (path) this.group.append(path);

            // code hint and file info
            const beats = this.buffer.duration * (this.audio.bpm / 60);
            this.setHTML('#file-path', `${this.name} (${this.formatBytes(this.sizeInBytes)})`);
            this.setHTML('#code-hint', `playSample("${this.name}", beats = ${Math.ceil(beats / 0.25) * 0.25})`);
            this.removeClass('#copy-button', 'hidden');
        }
        catch (e) {
            console.log(e);
            this.removeClass('#error', 'hidden');
        }
    }


    /**
     * post audio data on the assets server
     */
    private async saveFileData(filename : string, data : string) : Promise<boolean> {
        try {
            const url = new URL(`/api/audio/${this.uuid}`, import.meta.url);
            const response = await fetch(url, {
                method: 'POST',
                body: JSON.stringify({ filename, data }),
                headers: { 'Content-type': 'application/json; charset=UTF-8' }
            });
            const json = await response.json();
            return (json.success === true);
        } catch (e) {
            return false;
        }
    }


    /**
     * delete audio data from the assets server
     */
    private async deleteFileData() : Promise<boolean> {
        try {
            const url = new URL(`/api/audio/${this.uuid}`, import.meta.url);
            const response = await fetch(url, {
                method: 'DELETE',
                headers: { 'Content-type': 'application/json; charset=UTF-8' }
            });
            const json = await response.json();
            return (json.success === true);
        } catch (e) {
            return false;
        }
    }


    /**
     * User can upload a new file to replace the existing one
     */
    private async replaceFile(file : File) {

        const LIMIT = 30 * (1024 ** 2); // 30 MB
        const name = this.makePythonSafe(file.name);

        // compute total size of all samples in the project including the new file
        let totalSize = 0;
        let collision = false;
        document.querySelectorAll('tunepad-sample').forEach(s => {
            totalSize += (s as TunePadSample).sizeInBytes;
            if (name === s.getAttribute('name')) collision = true;
        });
        totalSize += file.size;
        
        // error if the combined size is too big
        if (totalSize > LIMIT) {
            window.alert("Error: Projects can only contain 30MB of combined audio samples. Try uploading a smaller sound file.");
            return;
        }

        /// check for name collision
        if (collision) {
            window.alert('Error: There is already a sample with this file name. Try uploading a different sound file.');
            return;
        }

        // set file name
        this.setAttribute('name', name);

        // decode audio data
        const abuf = await file.arrayBuffer();
        this.loadArrayBuffer(abuf);

        // save base64 encoded file data
        const reader = new FileReader();
        reader.onerror = (error) => { console.log(error) };
        reader.onload = async () => {
            const b64data = reader.result as string;
            await this.saveFileData(this.name, b64data);

            this.dispatchEvent(new CustomEvent("update-sample", {
                bubbles: false,
                composed: true,
                detail: { uuid : this.uuid, name : this.name }
            }));
        };
        reader.readAsDataURL(file);
    }


    /**
     * Return a serialized snapshot of state
     */
    public save() : any {
        return {
            uuid : this.uuid,
            name : this.name,
            type : TunePadSample.ELEMENT
        };
    }

    /**
     * Restore from a saved version
     */
    public async restore(data : any, loadPython : boolean = false) {
        if (data.uuid === this.uuid && data.name === this.name && this.buffer) {
            return;
        }
        this.setAttribute('uuid', data.uuid);
        this.setAttribute('name', data.name);

        try {
            const url = new URL(`/api/audio/${this.uuid}`, import.meta.url);
            const response = await fetch(url);
            const json = await response.json();
            if (json.success && json.sample) {
                const decoded = await fetch(json.sample.data);
                const arrayBuffer = await decoded.arrayBuffer();
                this.loadArrayBuffer(arrayBuffer);
            }
        } catch (e) {
            console.log('restore error');
        }
    }


    private playPauseAudio() {
        const ctx = this.audio.context;
        if (this.absn) {
            this.root.querySelector('#play-button')?.classList.remove('hidden');
            this.root.querySelector('#pause-button')?.classList.add('hidden');
            this.absn.stop();
        } else if (this.buffer) {
            this.root.querySelector('#play-button')?.classList.add('hidden');
            this.root.querySelector('#pause-button')?.classList.remove('hidden');

            this.absn = new AudioBufferSourceNode(ctx, { buffer : this.buffer });
            this.absn.connect(ctx.destination);
            this.absn.start(0, this._elapsed);
            const start = ctx.currentTime;
            const duration = this.buffer.duration;
            const animation = window.setInterval(() => {
                const d = (ctx.currentTime - start) + this._elapsed;
                this.setProgressBar(d / duration);
            }, 80);
            this.absn.addEventListener('ended', e => {
                this.absn = undefined;
                const d = (ctx.currentTime - start) + this._elapsed;
                this._elapsed = (d >= duration - 5) ? 0 : d;
                window.clearInterval(animation);
                this.setProgressBar(this._elapsed / duration);
                this.root.querySelector('#play-button')?.classList.remove('hidden');
                this.root.querySelector('#pause-button')?.classList.add('hidden');
            });
        }
    }
    private _elapsed = 0;


    private setProgressBar(p : number) {
        const progress = this.root.querySelector('#progress');
        progress?.setAttribute('style', `width: ${Math.round(p * 100)}%`);
    }

    /**
     * file size as string
     */
    private formatBytes(bytes : number) : string {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Math.round(bytes / Math.pow(k, i)) + ' ' + sizes[i];
    }


    /** 
     * Convert a string into a legal python file path name
     */
    private makePythonSafe(name: string) {
        let result = '';
        for (const char of name) {
            let c = char.charCodeAt(0);
            if ((c >= 48 && c <= 57) ||    // 0-9
                (c >= 97 && c <= 199) ||   // a-z
                (c >= 65 && c <= 90) ||    // A-Z
                [ 46, 47, 95 ].includes(c))   // '.' or '_' or '/'
            {
                result += char;
            }
            else if (c === 32) { // SPACE
                result += '_';
            }
            else if (c === 45) { // dash
                result += '_';
            }
        }

        if (result === '') {
            result = 'sample';
        }

        // python identifiers can't start with a number
        if (result.charCodeAt(0) >= 48 && result.charCodeAt(0) <= 57) {
            result = '_' + result;
        }
        return result;
    }


    private setHTML(selector : string, html : string) {
        const e = this.root.querySelector(selector);
        if (e) e.innerHTML = html;
    }

    private addClass(selector : string, className : string) {
        this.root.querySelector(selector)?.classList.add(className);
    }

    private removeClass(selector : string, className : string) {
        this.root.querySelector(selector)?.classList.remove(className);
    }
}
